import Button from '@ui/Button';
import { ProductDocument, Variation } from 'models/product';
import { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeContext } from 'context/StripeContext';
import { isMobile } from '@util/index';
import { useToastContext } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { usePathname, useRouter } from 'next/navigation';
import { hasValidVariations } from '@util/firestore/products';
import { StepType } from '../types';
import DesktopMakeAnOfferModal from './DesktopMakeAnOfferModal';
import MobileMakeAnOfferModal from './MobileMakeAnOfferModal';

interface MakeAnOfferButtonProps {
  product: ProductDocument;
  selectedVariations: Variation[] | null;
}

export default function MakeAnOfferButton({
  product,
  selectedVariations,
}: MakeAnOfferButtonProps) {
  const { userDoc, user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { stripePromise } = useStripeContext();
  const { showErrorToast } = useToastContext();
  const router = useRouter();
  const pathname = usePathname();

  const getStepList = (): { steps: StepType[]; totalSteps: number } => {
    if (!product.is_flat_rate) {
      // if the product has dynamic shipping options, add the shipping-options step
      return {
        steps: [
          'offer-price',
          'shipping-payment',
          'shipping-options',
          'review-offer',
        ],
        totalSteps: 4,
      };
    }
    return {
      steps: ['offer-price', 'shipping-payment', 'review-offer'],
      totalSteps: 3,
    };
  };

  const { steps, totalSteps } = getStepList();

  return (
    <>
      <Button
        type="outline"
        width="fluid"
        text="Make An Offer"
        disabled={product.out_of_stock}
        className="!text-[1.8rem]"
        onClick={() => {
          if (userDoc && !user?.isAnonymous) {
            if (!user?.phoneNumber && userDoc?.phone !== 'SKIP') {
              router.push('/verify-phone');
              return;
            }
            if (hasValidVariations(product, selectedVariations)) {
              setIsModalOpen(true);
            } else {
              showErrorToast('Please select all options');
            }
          } else {
            router.push(`/login?redirect=${encodeURI(pathname ?? '')}`);
          }
        }}
      />

      <Elements stripe={stripePromise}>
        {isMobile() ? (
          <MobileMakeAnOfferModal
            isOpen={isModalOpen}
            dismiss={() => setIsModalOpen(false)}
            product={product}
            selectedVariations={selectedVariations}
            stepData={{ steps, totalSteps }}
            navigationBar={
              <NavigationBar stepCount={totalSteps} currentStep={activeStep} />
            }
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        ) : (
          <DesktopMakeAnOfferModal
            isOpen={isModalOpen}
            dismiss={() => setIsModalOpen(false)}
            product={product}
            selectedVariations={selectedVariations}
            stepData={{ steps, totalSteps }}
            navigationBar={
              <NavigationBar stepCount={totalSteps} currentStep={activeStep} />
            }
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        )}
      </Elements>
    </>
  );
}

const NavigationBar = ({
  stepCount,
  currentStep,
}: {
  stepCount: number;
  currentStep: number;
}) => (
  <div className="my-4 flex justify-between gap-x-4 p-4">
    {Array.from({ length: stepCount }).map((_, index) => {
      const isActive = index === currentStep;

      return (
        <div
          key={index}
          className={`h-1 w-full rounded-full ${
            isActive ? 'bg-brand-secondary' : 'bg-zinc-200'
          }`}
        />
      );
    })}
  </div>
);
